import url from 'url';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'core/libs/router';

import withTheme from 'core/hocs/withTheme';

import { resolveScopedStyles } from 'core/utils/styled-jsx';
import { createUriFromLocation, decodeURL } from 'core/utils/url-helper';

import themePropTypes from 'core/utils/prop-types/theme';


function checkSpecialOrFollowLink(string, links) {
  if (!links?.length) return false;

  return links.some(rule => {
    if (typeof(rule) === 'string') {
      return rule === string.replace(/\/$/, '');
    }

    if (typeof(rule) === 'function') {
      return !!rule(string);
    }

    return false;
  });
}

/**
 * Компонент реализующий компонент ссылки интегрированный с роутером
 * и поддерживающий стилизацию через темы.
 *
 * Использую компонент
 * [Link](https://reacttraining.com/react-router/web/api/Link)
 * из `react-router`.
 *
 * @author Anton Ignatov <a.ignatov@rambler-co.ru>
 */
function Link(props, context) {
  const {
    to,
    type,
    theme,
    className,
    innerRef,
    isCurrent,
    dataQa,
    followExternal,
    ...otherProps
  } = props;

  const link = theme.controls.link[type];

  const scope = link && resolveScopedStyles(
    <scope>
      <style jsx>{`
        .link
          color ${link.idle.color}
          text-decoration ${link.idle.decoration}
          transition color ${theme.animations.hover}
          cursor pointer

          &:visited
            color ${link.visited.color}
            text-decoration ${link.visited.decoration}

          &:hover
            color ${link.hover.color}
            text-decoration ${link.hover.decoration}

          &:active
            color ${link.active.color}
            text-decoration ${link.active.decoration}

          &._isCurrent
            cursor default
            color ${(link.current || link.hover).color}
            text-decoration ${(link.current || link.hover).decoration}
      `}</style>
    </scope>
  );

  const resolvedClassName = cx(
    scope && scope.wrapClassNames('link', isCurrent && '_isCurrent'),
    className
  );

  const toIsObject = to && typeof to === 'object';

  const uri = toIsObject ? createUriFromLocation(to) : to;
  const href = !uri.indexOf('//') ? `https://${uri.replace(/^\/\//, '')}` : uri;

  const descriptor = url.parse(decodeURL(href));
  const isSpecialLink = descriptor.pathname && checkSpecialOrFollowLink(descriptor.pathname, context.specialLinks);
  const isInternalProtocol = !descriptor.protocol || /^https?/.test(descriptor.protocol);
  const isInternalHost = !descriptor.hostname || descriptor.hostname === context.host;
  const isInternalLink = otherProps.target !== '_blank' && isInternalProtocol && isInternalHost;

  if (!isSpecialLink && isInternalLink) {
    const linkTo = toIsObject
      ? to
      : [descriptor.path, descriptor.hash].filter(Boolean).join('');

    return (
      <RouterLink
        {...otherProps}
        innerRef={innerRef}
        {...resolvedClassName && { className: resolvedClassName }}
        to={linkTo}
        {...dataQa && { 'data-qa': dataQa }}
      >
        {scope && <scope.styles />}
        {otherProps.children}
      </RouterLink>
    );
  }

  /**
   * Находим внешние ссылки, которые НЕ нужно исключать из ранжирования поисковыми системами.
   * Спецссылки, телефон/почта, ссылки с поддоменами.
   * https://jira.rambler-co.ru/browse/UCMS-3067
   */
  const followLink = descriptor.hostname + descriptor.pathname;
  const isFollowLink = followLink && checkSpecialOrFollowLink(followLink, context.followExternalLinks);
  const isSubdomain = descriptor.hostname && Boolean(~descriptor.hostname.indexOf(context.host));
  const includeSearchRanging = isSpecialLink || isFollowLink || !isInternalProtocol || isSubdomain || followExternal;

  const relValues = [];

  if (!includeSearchRanging) {
    relValues.push('nofollow');
  }
  if (!isInternalLink) {
    relValues.push('noreferrer', 'noopener');
  }

  const relString = relValues.length > 0 ? relValues.join(' ') : '';

  return (
    /*
    * Атрибут aria-label устраняет замечание Google Lighthouse "Links do not have a discernible name"
    * Все свойства типа 'aria-*' обязательно должны писаться в кебаб-кейсе
    */
    <a
      {...relString && { rel: relString }}
      {...!isInternalLink && { target: '_blank' }}
      {...otherProps}
      ref={innerRef}
      href={descriptor.href}
      {...resolvedClassName && { className: resolvedClassName }}
      {...dataQa && { 'data-qa': dataQa }}
    >
      {scope && <scope.styles />}
      {otherProps.children}
    </a>
  );
}

Link.contextTypes = {
  host: PropTypes.string,
  specialLinks: PropTypes.array,
  followExternalLinks: PropTypes.array,
};

const stateContract = `
  {
    color,
    decoration,
  }
`;

const typeContract = `
  {
    idle: ${stateContract},
    hover: ${stateContract},
    active: ${stateContract},
    visited: ${stateContract},
  }
`;

Link.propTypes = {
  // TODO: Добавить поддержку функций

  /**
   * Путь для перехода при клике на ссылку
   */
  to: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]).isRequired,

  /** @ignore */
  className: PropTypes.string,

  /**
   * Функция, получающая в качестве аргумента ref отрендеренной ссылки
   */
  innerRef: PropTypes.func,

  /** @ignore */
  theme: themePropTypes(`
    {
      animations: {
        hover,
      },
      controls: {
        link: {
          article: ${typeContract},
          secondary: ${typeContract},
          tertiary: ${typeContract},
        },
      },
    }
  `),
  /**
   * Тип ссылки (см. спецификацию тем).
   */
  type: PropTypes.string.isRequired,

  /**
   * Стилизует ссылку как выбранную (если совпадает с URL страницы).
   */
  isCurrent: PropTypes.bool,


  /**
   * Не добавляет rel=nofollow для внешних ссылок
   * явная передача rel имеет приоритет
   */
  followExternal: PropTypes.bool,

  /** @ignore */
  dataQa: PropTypes.string,
};

Link.defaultProps = {
  type: 'article',
};

const LinkWithHOCs = withTheme(Link);
LinkWithHOCs.displayName = 'Link';

export { Link as StorybookComponent };
export default LinkWithHOCs;
